import { h } from 'preact';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { useRecsProvider } from '../search/useSearchProvider';
import { BirdsnestProductCard } from '../components/product/BirdsnestProductCard';
import { OutfitCard } from '../components/product/OutfitCard';
import { isOutfitProduct } from '../utils/helpers';
import styles from './ProductRecommendationsSection.module.scss';
import Flickity from 'flickity';
import { trackViewItemList } from '../utils/elevar';
export function ProductRecommendationsSection({ containerElement = null }) {
    const carouselRef = useRef(null);
    const flickityInstanceRef = useRef(null);
    const mountEl = containerElement !== null && containerElement !== void 0 ? containerElement : document.getElementById('recommendation-app');
    // Prefer HTML content from an immediate sibling <script type="text/plain"> next to the recommendation-app element
    // (either previous or next sibling). If no such neighbour exists, no heading is rendered.
    // TODO: The current styling inherit from existing styling from Birdsnest theme. Need to update the styling to be standalone in this Preact component.
    const headingHtml = (() => {
        if (!mountEl || typeof document === 'undefined')
            return '';
        const checkScriptSibling = (el) => {
            var _a;
            if (!el || !(el instanceof HTMLScriptElement))
                return '';
            return el.type === 'text/plain' ? (_a = el.textContent) !== null && _a !== void 0 ? _a : '' : '';
        };
        const fromPrev = checkScriptSibling(mountEl.previousElementSibling);
        if (fromPrev) {
            return fromPrev;
        }
        const fromNext = checkScriptSibling(mountEl.nextElementSibling);
        if (fromNext) {
            return fromNext;
        }
        // No neighbouring <script type="text/plain"> found; do not render a heading.
        return '';
    })();
    const recsConfig = useMemo(() => {
        var _a, _b;
        if (!mountEl)
            return undefined;
        const seedProductId = (_a = mountEl.getAttribute('data-seed-product-id')) !== null && _a !== void 0 ? _a : '';
        const recsType = (_b = mountEl.getAttribute('data-recommendation-type')) !== null && _b !== void 0 ? _b : 'pdp_ymal';
        return { seedProductId, recsType };
    }, [mountEl]);
    // Use the useRecsProvider hook with test data
    // Mock/override: pass recsConfig to use seedProductId & recsType explicitly instead of from the mount element, e.g.:
    // { seedProductId: window.liquidCustom?.productIdRecommendation || "", recsType: 'pdp_ymal' }
    const { loading, products, meta } = useRecsProvider('fredhopper', '', recsConfig);
    // Simple layout configuration
    const layout = {
        cardLayout: 'gridFour',
        filterLayout: 'filterTop'
    };
    const hasItems = products.length > 0;
    const productKey = useMemo(() => (hasItems ? products.map((p) => p.id).join('-') : ''), [products, hasItems]);
    // Precompute outfit IDs once when products change; use Set.has() in render instead of
    // calling isOutfitProduct(product) per item on every render (avoids repeated work and keeps render cheap).
    const outfitProductIds = useMemo(() => new Set(products.filter(isOutfitProduct).map((p) => p.id)), [products]);
    const trackingListId = (recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.recsType)
        ? `fh_recs_${recsConfig.recsType}`
        : undefined;
    useEffect(() => {
        if (!hasItems || loading || !trackingListId)
            return;
        trackViewItemList(products, trackingListId);
    }, [hasItems, loading, productKey, trackingListId]);
    useEffect(() => {
        if (!hasItems || loading) {
            return;
        }
        let rafId = null;
        const initSlider = () => {
            if (!carouselRef.current) {
                return;
            }
            if (!carouselRef.current.offsetWidth) {
                rafId = requestAnimationFrame(initSlider);
                return;
            }
            if (flickityInstanceRef.current) {
                flickityInstanceRef.current.destroy();
                flickityInstanceRef.current = null;
            }
            flickityInstanceRef.current = new Flickity(carouselRef.current, {
                prevNextButtons: true,
                pageDots: false,
                wrapAround: false,
                contain: true,
                draggable: true,
                cellAlign: 'left',
                groupCells: false,
                imagesLoaded: true,
            });
            flickityInstanceRef.current.resize();
        };
        initSlider();
        const handleResize = () => {
            var _a;
            (_a = flickityInstanceRef.current) === null || _a === void 0 ? void 0 : _a.resize();
        };
        window.addEventListener('resize', handleResize);
        // Cells can grow after Flickity's initial measurement (async card content),
        // so resize whenever a cell's height changes to keep the viewport in sync
        let resizeObserver = null;
        if (carouselRef.current && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                var _a;
                (_a = flickityInstanceRef.current) === null || _a === void 0 ? void 0 : _a.resize();
            });
            carouselRef.current.querySelectorAll(`.${styles.sliderCell}`).forEach((cell) => {
                resizeObserver.observe(cell);
            });
        }
        return () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            window.removeEventListener('resize', handleResize);
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
            if (flickityInstanceRef.current) {
                flickityInstanceRef.current.destroy();
                flickityInstanceRef.current = null;
            }
        };
    }, [hasItems, productKey, loading]);
    if (loading || !hasItems) {
        return null;
    }
    return (h("div", { class: styles.recommendationsSection },
        headingHtml ? (h("div", { class: styles.title, dangerouslySetInnerHTML: {
                __html: headingHtml,
            } })) : null,
        h("div", { class: styles.sliderWrapper },
            h("div", { class: styles.slider, ref: carouselRef }, products.map((product, index) => (h("div", { class: styles.sliderCell, key: `recs-${product.id}` }, outfitProductIds.has(product.id) ? (h(OutfitCard, { product: product, cardBackground: "#F6F5F2", cellBackground: "#FFFFFF" })) : (h(BirdsnestProductCard, { product: product, layout: layout.cardLayout, trackingListId: trackingListId, trackingPosition: index })))))))));
}
